<template>
  <v-dialog v-model="dialog" max-width="600" @keydown.esc="cancel()" persistent :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title class="white--text">{{ newItem ? 'Uusi nimike' : 'Muokkaa nimikettä' }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cancel">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-5 gradient-icons">
        <v-form ref="form">
          <v-text-field
            :disabled="saving || !isItemEditable"
            outlined
            required
            dense
            :label="$t('items.item_name')"
            :rules="[$rules.required]"
            v-model="item.name"
            validate-on-blur
            v-on:keydown.enter.prevent="newItem ? createItem : saveItem"
            clearable

          >
            <template v-slot:prepend>
              <v-icon>mdi-text</v-icon>
            </template>
          </v-text-field>

          <v-autocomplete
            :disabled="saving || !isItemEditable"
            v-model="item.typeId"
            :rules="[$rules.required]"
            :items="itemTypes"
            item-text="name"
            item-value="value"
            :label="$t('items.item_type')"
            outlined
            dense
            validate-on-blur
            v-on:keydown.enter.prevent="newItem ? createItem : saveItem"
            @change="onTypeChange"
          >
            <template v-slot:prepend>
              <v-icon>mdi-cards-variant</v-icon>
            </template>
          </v-autocomplete>

          <v-autocomplete
            v-if="showUnit"
            :disabled="saving || !isItemEditable"
            v-model="item.unit"
            :items="unitOptions"
            item-text="name"
            item-value="value"
            :label="$t('items.unit')"
            outlined
            dense
            clearable
            validate-on-blur
          >
            <template v-slot:prepend>
              <v-icon>mdi-ruler</v-icon>
            </template>
          </v-autocomplete>

          <v-text-field
            v-if="canEditPrice"
            :disabled="saving || !isItemEditable"
            v-model="priceDisplay"
            :label="$t('items.price')"
            :suffix="$t('items.price_unit')"
            :hint="$t('items.price_hint')"
            persistent-hint
            outlined
            dense
            clearable
            validate-on-blur
            :rules="[priceRule]"
            @input="onPriceInput"
          >
            <template v-slot:prepend>
              <v-icon>mdi-currency-eur</v-icon>
            </template>
          </v-text-field>

          <v-autocomplete
            v-if="showWarehouseUnit"
            :disabled="saving || !isItemEditable"
            v-model="item.warehouseUnit"
            :items="unitOptions"
            item-text="name"
            item-value="value"
            :label="$t('items.warehouse_unit')"
            outlined
            dense
            clearable
            validate-on-blur
          >
            <template v-slot:prepend>
              <v-icon>mdi-warehouse</v-icon>
            </template>
          </v-autocomplete>

          <numeric-input
            v-if="showWarehouseUnit"
            :disabled="saving || !isItemEditable"
            v-model="item.conversionFactor"
            :options="conversionFactorOptions"
            :label="$t('items.conversion_factor')"
            :hint="$t('items.conversion_factor_hint')"
            :rules="item.warehouseUnit ? [$rules.required] : []"
            persistent-hint
            outlined
            dense
            clearable
            validate-on-blur
          >
            <template v-slot:prepend>
              <v-icon>mdi-swap-horizontal</v-icon>
            </template>
          </numeric-input>
        </v-form>
        <v-alert :value="showAlert" outlined type="error">{{ $t('validation.check_all_required_fields') }}</v-alert>
        <confirm-dialog ref="confirmTypeChange" />
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions :class="$vuetify.breakpoint.xsOnly ? 'flex-column-reverse' : ''">
        <v-spacer></v-spacer>
        <v-btn
          :fab="$vuetify.breakpoint.xsOnly"
          :bottom="$vuetify.breakpoint.xsOnly"
          :left="$vuetify.breakpoint.xsOnly"
          :fixed="$vuetify.breakpoint.xsOnly"
          :class="$vuetify.breakpoint.xsOnly ? 'ma-0 pa-0' : ''"
          color="default"
          @click.native="cancel()"
          ><v-icon v-if="$vuetify.breakpoint.xsOnly">mdi-close</v-icon
          ><span v-if="$vuetify.breakpoint.smAndUp">{{ $t('cancel') }}</span></v-btn
        >
        <v-btn
          :fab="$vuetify.breakpoint.xsOnly"
          :bottom="$vuetify.breakpoint.xsOnly"
          :right="$vuetify.breakpoint.xsOnly"
          :fixed="$vuetify.breakpoint.xsOnly"
          :class="$vuetify.breakpoint.xsOnly ? 'ma-0 pa-0' : ''"
          v-if="!newItem && isItemEditable"
          color="primary"
          @click.native="saveItem()"
          ><v-icon v-if="$vuetify.breakpoint.xsOnly">mdi-content-save</v-icon
          ><span v-if="$vuetify.breakpoint.smAndUp">{{ $t('save') }}</span></v-btn
        >
        <v-btn
          :fab="$vuetify.breakpoint.xsOnly"
          :bottom="$vuetify.breakpoint.xsOnly"
          :right="$vuetify.breakpoint.xsOnly"
          :fixed="$vuetify.breakpoint.xsOnly"
          :class="$vuetify.breakpoint.xsOnly ? 'ma-0 pa-0' : ''"
          v-if="newItem"
          color="primary"
          @click.native="createItem()"
          ><v-icon v-if="$vuetify.breakpoint.xsOnly">mdi-plus</v-icon
          ><span v-if="$vuetify.breakpoint.smAndUp">{{ $t('save') }}</span></v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import itemApi from '@/api/item';
import ConfirmDialog from '@/components/ConfirmDialog';
import NumericInput from '@/components/NumericInput';
import { allItemTypes, isEditable, isTrackable, hasUnit, hasWarehouseUnit, itemType, loadItemTypes, typeName } from '@/helpers/itemTypes';
const itemTemplate = {
  id: null,
  name: '',
  typeId: null,
  unit: null,
  price: null,
  warehouseUnit: null,
  conversionFactor: null,
};
export default {
  name: 'AddOrEditItemDialog',
  components: { ConfirmDialog, NumericInput },
  data() {
    return {
      unitOptions: [
        { name: 'kpl', value: 'kpl' },
        { name: 'm', value: 'm' },
        { name: 'kg', value: 'kg' },
        { name: 'l', value: 'l' },
        { name: 'm²', value: 'm2' },
        { name: 'm³', value: 'm3' },
        { name: 'kannu', value: 'kannu' },
      ],
      showAlert: false,
      title: '',
      newItem: null,
      saving: false,
      dialog: false,
      item: { ...itemTemplate },
      // Muokkauksessa: ladattu tyyppi (rajaa valinnat samaan käyttötarkoitukseen) ja viimeksi hyväksytty valinta peruutusta varten.
      originalTypeId: null,
      previousTypeId: null,
      priceDisplay: '',
      conversionFactorOptions: {
        decimalCharacter: ',',
        decimalCharacterAlternative: '.',
        digitGroupSeparator: ' ',
        decimalPlaces: 6,
        allowDecimalPadding: false,
        emptyInputBehavior: 'null',
      },
    };
  },
  computed: {
    // Uusi nimike vain käytössä olevaan tyyppiin. Muokkauksessa tyypin saa vaihtaa vain saman käyttötarkoituksen
    // aktiiviseen tyyppiin (backend valvoo samaa); nykyinen tyyppi näytetään, vaikka se olisi poistettu käytöstä.
    itemTypes() {
      const original = itemType(this.originalTypeId);
      return allItemTypes()
        .filter(type => (type.active || type.id === this.originalTypeId) && (this.newItem || !original || type.purpose === original.purpose))
        .map(type => ({ name: type.name, value: type.id }));
    },
    isItemEditable(){
      return this.newItem || isEditable(this.item.typeId)
    },
    showUnit() {
      return hasUnit(this.item.typeId);
    },
    showWarehouseUnit() {
      return hasWarehouseUnit(this.item.typeId);
    },
    isWarehouseUnitValid() {
      // Jos varastoyksikkö valittu, kerroin pakollinen
      return !this.item.warehouseUnit || (this.item.conversionFactor !== null && this.item.conversionFactor !== undefined && this.item.conversionFactor !== '');
    },
    canEditPrice() {
      // Price is only visible to admin users and only for trackable items
      const hasPermission = this.$isTenantAdmin;
      const isTrackableItem = isTrackable(this.item.typeId);
      return hasPermission && isTrackableItem;
    },
    priceRule() {
      return (value) => {
        if (!value) return true;
        // Allow numbers with comma or dot as decimal separator
        const pattern = /^[0-9]+([,.][0-9]{0,2})?$/;
        return pattern.test(value) || 'Virheellinen hinta';
      };
    },
  },
  methods: {
    open(itemId, newItem, typeId) {
      loadItemTypes().catch(error => this.$handleApiError(error));
      this.dialog = true;
      this.newItem = newItem;
      this.$refs.form?.reset();
      this.item = { ...itemTemplate };
      this.originalTypeId = null;
      this.previousTypeId = null;
      this.priceDisplay = '';
      if (!this.newItem) {
        this.item.id = itemId;
        this.getItem();
      } else {
        this.item.typeId = typeId;
      }
    },
    cancel() {
      this.item.id = '';
      this.item.name = '';
      this.item.typeId = null;
      this.item.unit = null;
      this.item.price = null;
      this.item.warehouseUnit = null;
      this.item.conversionFactor = null;
      this.priceDisplay = '';
      this.newItem = null;
      this.showAlert = false;
      this.dialog = false;
    },
    onPriceInput(value) {
      if (!value) {
        this.item.price = null;
        return;
      }
      // Convert comma to dot for parsing
      const normalized = value.replace(',', '.');
      const parsed = parseFloat(normalized);
      this.item.price = isNaN(parsed) ? null : parsed;
    },
    formatPriceForDisplay(price) {
      if (price === null || price === undefined) return '';
      return String(price).replace('.', ',');
    },
    // Arvot, joita uusi tyyppi ei tue ja jotka nimikkeellä on: ne tyhjennetään vaihdossa. Uuden tyypin tukemat arvot säilyvät.
    droppedValues(typeId) {
      const dropped = [];
      if (!hasUnit(typeId) && this.item.unit) dropped.push({ label: this.$t('items.unit'), value: this.item.unit, fields: ['unit'] });
      if (!isTrackable(typeId) && this.item.price != null) {
        dropped.push({ label: this.$t('items.price'), value: `${this.formatPriceForDisplay(this.item.price)} €`, fields: ['price'] });
      }
      if (!hasWarehouseUnit(typeId)) {
        if (this.item.warehouseUnit) {
          dropped.push({ label: this.$t('items.warehouse_unit'), value: this.item.warehouseUnit, fields: ['warehouseUnit'] });
        }
        if (this.item.conversionFactor != null) {
          dropped.push({ label: this.$t('items.conversion_factor'), value: this.item.conversionFactor, fields: ['conversionFactor'] });
        }
      }
      return dropped;
    },
    // Muokkauksessa tyypin vaihto vahvistetaan, jos arvoja tyhjenee; peruutus palauttaa edellisen tyypin.
    async onTypeChange(typeId) {
      if (this.newItem || typeId == null || typeId === this.previousTypeId) return;
      const dropped = this.droppedValues(typeId);
      if (dropped.length > 0) {
        const values = dropped.map(d => `${d.label.toLowerCase()} (${d.value})`).join(', ');
        const confirmed = await this.$refs.confirmTypeChange.open(
          this.$t('items.type_change.title'),
          this.$t('items.type_change.description', { type: typeName(typeId), values }),
          { color: 'warning', agree: this.$t('items.type_change.agree') }
        );
        if (!confirmed) {
          this.item.typeId = this.previousTypeId;
          return;
        }
        dropped.flatMap(d => d.fields).forEach(field => {
          this.item[field] = null;
        });
        if (this.item.price == null) this.priceDisplay = '';
      }
      this.previousTypeId = typeId;
    },
    async saveItem() {
      if (!this.item.name || !this.isWarehouseUnitValid) {
        this.showAlert = true;
        return;
      }

      try {
        this.saving = true;
        if (!this.item.warehouseUnit) this.item.conversionFactor = null;
        await itemApi.updateItem(this.item.id, this.item);
        this.$emit('updated');
        this.cancel();
      } catch (err) {
        if (err.response && err.response.status === 409) {
          this.$showErrorNotification(this.$t('items.type_change.inventory_in_use'));
          // Palautetaan tallennettu tila, jotta dialogiin tyhjennetyt arvot eivät jää odottamaan uutta tallennusta
          await this.getItem();
        } else {
          this.$handleApiError(err);
        }
      }
      this.saving = false;
    },
    async createItem() {
      if (!this.item.name || !this.item.typeId || !this.isWarehouseUnitValid) {
        this.showAlert = true;
        return;
      }

      try {
        this.saving = true;
        if (!this.item.warehouseUnit) this.item.conversionFactor = null;
        await itemApi.createItem(this.item);
        this.$emit('created');
        this.cancel();
      } catch (err) {
        this.$handleApiError(err);
      }
      this.saving = false;
    },
    async getItem() {
      this.saving = true;

      this.item = await itemApi.getItem(this.item.id);
      this.originalTypeId = this.item.typeId;
      this.previousTypeId = this.item.typeId;
      this.priceDisplay = this.formatPriceForDisplay(this.item.price);
      this.saving = false;
    },
  },
};
</script>

<style>
.bottom_fixed {
  position: fixed;
  bottom: 0;
}
</style>
